import apiClient from './api';
import type { Contact, CreateContactRequest, UpdateContactRequest, ContactSearchParams } from '../types/contact';
import type { PagedResponse } from '../types/common';
import type { ImportColumns, ImportPreview, ImportResult } from '../types/import';
import { excelImportClient } from './excelImportClient';

const CONTACT_IMPORT_PATH = '/contacts/import';

export const contactService = {
  // Import contacts from an .xlsx file
  previewImport: (file: File): Promise<ImportPreview> => excelImportClient.preview(CONTACT_IMPORT_PATH, file),

  importFromExcel: (companyId: number, file: File, columns: ImportColumns): Promise<ImportResult> =>
    excelImportClient.run(CONTACT_IMPORT_PATH, companyId, file, columns),

  search: async (companyId: number, params?: ContactSearchParams): Promise<PagedResponse<Contact>> => {
    const response = await apiClient.post<PagedResponse<Contact>>('/contacts/search', {
      companyId,
      type: params?.type,
      search: params?.search,
      phone: params?.phone,
      includeArchived: params?.includeArchived ?? false,
      page: params?.page ?? 0,
      size: params?.size ?? 20,
      sortBy: params?.sortBy ?? 'id',
      sortDir: params?.sortDir ?? 'asc',
    });
    return response.data;
  },

  getById: async (contactId: number): Promise<Contact> => {
    const response = await apiClient.get<Contact>(`/contacts/${contactId}`);
    return response.data;
  },

  // Existing Contact of the company with this exact email, or null (BE answers 404)
  getByEmail: async (companyId: number, email: string): Promise<Contact | null> => {
    const response = await apiClient.get<Contact>('/contacts/by-email', {
      params: { companyId, email },
      validateStatus: (status) => status === 200 || status === 404,
    });
    return response.status === 404 ? null : response.data;
  },

  create: async (data: CreateContactRequest): Promise<Contact> => {
    const response = await apiClient.post<Contact>('/contacts', data);
    return response.data;
  },

  update: async (contactId: number, data: UpdateContactRequest): Promise<Contact> => {
    const response = await apiClient.put<Contact>(`/contacts/${contactId}`, data);
    return response.data;
  },

  delete: async (contactId: number): Promise<void> => {
    await apiClient.delete(`/contacts/${contactId}`);
  },

  restore: async (contactId: number): Promise<void> => {
    await apiClient.post(`/contacts/${contactId}/restore`);
  },
};
